import React, { useState, useEffect, useCallback } from 'react';
import { adminConfigService } from '../../services/AdminConfigService';
import {
  Save,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Check,
  Rocket,
  Undo2,
  FlaskConical,
  Globe,
} from 'lucide-react';

/**
 * Editor do prompt que planeja as gerações de imagem (gpt-image).
 *
 * Existem dois "slots" do mesmo prompt:
 *  - mode="production" → o que roda para os clientes
 *  - mode="draft"      → o que roda só no Laboratório (testes sem impacto)
 *
 * O mesmo componente atende os dois: muda o endpoint, o aviso do topo
 * e as ações extras (promover / descartar) que só existem no rascunho.
 */
const ImagePromptEditor = ({ mode = 'production', onPromoted = null }) => {
  const isDraft = mode === 'draft';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [variables, setVariables] = useState({});
  const [meta, setMeta] = useState({ isDefault: true, mirrorsProduction: false, updatedAt: null });

  const [form, setForm] = useState({
    system_prompt: '',
    user_prompt_template: '',
    temperature: 0.7,
    max_tokens: 2200,
  });

  const applyPlanner = useCallback((data) => {
    const p = data.planner || {};
    setForm({
      system_prompt: p.system_prompt || '',
      user_prompt_template: p.user_prompt_template || '',
      temperature: p.temperature ?? 0.7,
      max_tokens: p.max_tokens ?? 2200,
    });
    setMeta({
      isDefault: !!p.is_default,
      mirrorsProduction: !!p.mirrors_production,
      updatedAt: p.updated_at || null,
    });
    if (data.variables) setVariables(data.variables);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = isDraft
        ? await adminConfigService.getImagePlannerDraft()
        : await adminConfigService.getImagePlanner();
      applyPlanner(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível carregar o prompt.');
    } finally {
      setLoading(false);
    }
  }, [isDraft, applyPlanner]);

  useEffect(() => { load(); }, [load]);

  // Some com o aviso de sucesso sozinho para não acumular na tela.
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        system_prompt: form.system_prompt,
        user_prompt_template: form.user_prompt_template,
        temperature: Number(form.temperature),
        max_tokens: Number(form.max_tokens),
      };
      const data = isDraft
        ? await adminConfigService.updateImagePlannerDraft(payload)
        : await adminConfigService.updateImagePlanner(payload);
      applyPlanner(data);
      setSuccess(data.message || 'Prompt salvo.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao salvar o prompt.');
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    const ok = window.confirm(
      'Promover este prompt para produção?\n\n' +
      'Ele passará a ser usado em TODAS as gerações de imagem dos clientes.'
    );
    if (!ok) return;
    setActing(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminConfigService.promoteImagePlannerDraft();
      setSuccess(data.message || 'Rascunho promovido para produção.');
      if (onPromoted) onPromoted();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao promover o rascunho.');
    } finally {
      setActing(false);
    }
  };

  const handleReset = async () => {
    const ok = window.confirm(
      'Descartar o rascunho?\n\nO Laboratório volta a usar o prompt que está em produção.'
    );
    if (!ok) return;
    setActing(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminConfigService.resetImagePlannerDraft();
      applyPlanner(data);
      setSuccess(data.message || 'Rascunho descartado.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao descartar o rascunho.');
    } finally {
      setActing(false);
    }
  };

  const insertVariable = (name) => {
    setForm((prev) => ({
      ...prev,
      user_prompt_template: `${prev.user_prompt_template}{{${name}}}`,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando prompt...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Aviso de contexto — deixa explícito o que este editor afeta */}
      {isDraft ? (
        <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-2xl">
          <FlaskConical className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Rascunho do Laboratório — produção não é afetada</p>
            <p>
              As gerações feitas aqui no Lab usam este prompt. Os clientes continuam
              usando o prompt de produção até você clicar em <b>Promover para produção</b>.
            </p>
            {meta.mirrorsProduction && (
              <p className="mt-2 text-blue-700">
                No momento este rascunho é uma cópia do prompt de produção.
                Edite e salve para começar a testar uma versão diferente.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <Globe className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">Prompt de produção — afeta todos os clientes</p>
            <p>
              Alterações aqui valem imediatamente para as próximas gerações de imagem.
              Para testar sem risco, use o editor dentro do Laboratório.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
        {meta.isDefault
          ? 'Usando o prompt padrão do sistema (nenhuma alteração salva ainda).'
          : `Prompt personalizado${meta.updatedAt ? ` · última alteração em ${new Date(meta.updatedAt).toLocaleString('pt-BR')}` : ''}.`}
      </div>

      {/* System prompt */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1">
          System prompt
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Define o papel do agente que planeja os posts.
        </p>
        <textarea
          value={form.system_prompt}
          onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
          rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
      </div>

      {/* User prompt template */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1">
          Template do prompt
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Instruções enviadas a cada lote. Clique numa variável para inserir no final do texto.
        </p>

        {Object.keys(variables).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.entries(variables).map(([name, desc]) => (
              <button
                key={name}
                type="button"
                title={desc}
                onClick={() => insertVariable(name)}
                className="px-2 py-1 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg text-[11px] font-mono text-amber-800 transition-colors"
              >
                {`{{${name}}}`}
              </button>
            ))}
          </div>
        )}

        <textarea
          value={form.user_prompt_template}
          onChange={(e) => setForm({ ...form, user_prompt_template: e.target.value })}
          rows={18}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mono text-xs focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
        {!form.user_prompt_template.includes('{{prompt}}') && (
          <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            O template precisa conter <span className="font-mono">{'{{prompt}}'}</span> — é o briefing do cliente.
          </p>
        )}
      </div>

      {/* Parâmetros do modelo */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Temperatura
          </label>
          <p className="text-xs text-gray-500 mb-2">0 = previsível · 2 = mais criativo</p>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={form.temperature}
            onChange={(e) => setForm({ ...form, temperature: e.target.value })}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Máximo de tokens
          </label>
          <p className="text-xs text-gray-500 mb-2">Entre 500 e 8000</p>
          <input
            type="number"
            step="100"
            min="500"
            max="8000"
            value={form.max_tokens}
            onChange={(e) => setForm({ ...form, max_tokens: e.target.value })}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        </div>
      </div>

      {/* Ações */}
      <div className="flex flex-wrap gap-3 pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || acting}
          className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isDraft ? 'Salvar rascunho' : 'Salvar em produção'}
        </button>

        <button
          type="button"
          onClick={load}
          disabled={saving || acting}
          className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 disabled:opacity-50 border border-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Recarregar
        </button>

        {isDraft && (
          <>
            <button
              type="button"
              onClick={handlePromote}
              disabled={saving || acting}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              Promover para produção
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving || acting}
              className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 disabled:opacity-50 border border-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
            >
              <Undo2 className="w-4 h-4" />
              Descartar rascunho
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImagePromptEditor;
